import { logger } from '../utils/logger';

import type { ApiError } from './types';

export type ApiClientOptions = {
  baseUrl?: string;
  getInitData?: () => string | null;
  onForbidden?: () => void;
};

export class HttpError extends Error {
  status: number;
  payload?: ApiError;

  constructor(status: number, message: string, payload?: ApiError) {
    super(message);
    this.status = status;
    this.payload = payload;
  }

  /** Stable machine-readable error code (`code`, falling back to the legacy `error` field). */
  get code(): string | undefined {
    return this.payload?.code ?? this.payload?.error;
  }

  get details(): Record<string, unknown> | undefined {
    return this.payload?.details;
  }
}

/** Returns true if the error is an HttpError with 401/403 (handled centrally by auto-refresh / onForbidden). */
export function isAuthError(error: unknown): boolean {
  return error instanceof HttpError && (error.status === 401 || error.status === 403);
}

function getCsrfToken(): string | null {
  const match = document.cookie.match(/(?:^|;\s*)XSRF-TOKEN=([^;]*)/);
  return match ? decodeURIComponent(match[1]) : null;
}

export function createApiClient(options: ApiClientOptions) {
  const baseUrl = options.baseUrl ?? '';
  let refreshPromise: Promise<boolean> | null = null;

  async function doFetch(path: string, init: RequestInit & { contentType?: string }): Promise<Response> {
    const headers = new Headers(init.headers);
    headers.set('Accept', 'application/json');
    if (init.body) {
      headers.set('Content-Type', init.contentType ?? 'application/json');
    }

    const csrfToken = getCsrfToken();
    if (csrfToken) {
      headers.set('X-XSRF-TOKEN', csrfToken);
    }

    return fetch(`${baseUrl}${path}`, {
      ...init,
      headers,
      credentials: 'include',
    });
  }

  async function refreshAuth(): Promise<boolean> {
    const hasGetter = typeof options.getInitData === 'function';
    const initData = options.getInitData?.();
    logger.info('API', `refreshAuth: hasGetter=${hasGetter}, initData=${initData ? `"${initData.slice(0, 40)}..."` : 'null'}`);
    if (!initData) return false;

    try {
      const response = await doFetch('/auth/telegram', {
        method: 'POST',
        body: initData,
        contentType: 'text/plain',
      });
      logger.info('API', `refreshAuth: POST /auth/telegram → ${response.status}`);
      return response.ok;
    } catch (err) {
      logger.error('API', `refreshAuth: POST /auth/telegram failed`, { error: err instanceof Error ? err.message : String(err) });
      return false;
    }
  }

  async function request<T>(path: string, init: RequestInit & { contentType?: string }): Promise<T> {
    const method = init.method || 'GET';
    const startTime = performance.now();

    logger.info('API', `→ ${method} ${path}`);

    let response: Response;
    try {
      response = await doFetch(path, init);
    } catch (error) {
      const duration = Math.round(performance.now() - startTime);
      logger.error('API', `✗ ${method} ${path} FAILED ${duration}ms`, {
        error: error instanceof Error ? error.message : String(error),
        path,
      });
      throw error;
    }

    // Auto-refresh on 401: re-authenticate and retry once
    // Use promise-based locking to handle concurrent requests gracefully
    // Login endpoints (/auth/*) report their own 401 and must not trigger a re-login loop.
    if (response.status === 401 && !path.startsWith('/auth/')) {
      if (!refreshPromise) {
        logger.info('API', `Auto-refresh triggered (path=${path})`);
        refreshPromise = refreshAuth().finally(() => {
          refreshPromise = null;
        });
      } else {
        logger.info('API', `Auto-refresh already in progress, waiting (path=${path})`);
      }

      const refreshed = await refreshPromise;
      logger.info('API', `Auto-refresh result: ${refreshed}`);
      if (refreshed) {
        response = await doFetch(path, init);
      }
    }

    const duration = Math.round(performance.now() - startTime);

    if (!response.ok) {
      let payload: ApiError | undefined;
      try {
        payload = (await response.json()) as ApiError;
      } catch {
        payload = undefined;
      }

      logger.error('API', `← ${method} ${path} [${response.status}] ${duration}ms`, {
        status: response.status,
        path,
        message: payload?.message,
      });

      if (response.status === 403 && options.onForbidden) {
        options.onForbidden();
      }

      throw new HttpError(
        response.status,
        payload?.message ?? `HTTP ${response.status}`,
        payload,
      );
    }

    logger.info('API', `← ${method} ${path} [${response.status}] ${duration}ms`);

    // Some endpoints may return an empty body; treat as void.
    if (response.status === 204) {
      return undefined as T;
    }

    const text = await response.text();
    return (text ? JSON.parse(text) : undefined) as T;
  }

  return {
    get: <T>(path: string) => request<T>(path, { method: 'GET' }),
    post: <T>(path: string, body?: unknown) =>
      request<T>(path, { method: 'POST', body: body ? JSON.stringify(body) : undefined }),
    // Send raw string body without JSON.stringify (for initData, etc.)
    postRaw: <T>(path: string, body: string) =>
      request<T>(path, { method: 'POST', body, contentType: 'text/plain' }),
    patch: <T>(path: string, body?: unknown, headers?: Record<string, string>) =>
      request<T>(path, { method: 'PATCH', body: body ? JSON.stringify(body) : undefined, headers }),
    put: <T>(path: string, body?: unknown) =>
      request<T>(path, { method: 'PUT', body: body ? JSON.stringify(body) : undefined }),
    del: <T>(path: string) => request<T>(path, { method: 'DELETE' }),
  };
}
